import type { RefObject } from 'react';
import { Box } from '@mui/material';
import { layoutGap } from '../../../theme/theme';
import type { AvailableParameter, PropertyItem } from '../../../shared/ui/propertySelection/PropertySelectionPanel';
import type { UseExportColumnsReturn } from '../hooks/useExportColumns';
import type { ExportRowHighlightMap } from '../lib/rowHighlights';
import type { ExportFailuresBySystemName } from '../lib/exportFailurePresentation';
import type { PipingSystem } from '../types';
import ExportColumnPanels from './ExportColumnPanels';
import SystemTable from './SystemTable';

interface ExportSystemsWorkspaceProps {
  systems: PipingSystem[];
  selected: Set<string>;
  systemNameFilterValue: string;
  onSystemNameFilterChange: (value: string) => void;
  columns: UseExportColumnsReturn;
  loading: boolean;
  selectionDisabled: boolean;
  columnPanelOpen: boolean;
  useDrawer: boolean;
  persistentDrawer: boolean;
  panelProperties: PropertyItem[];
  availableParameters: AvailableParameter[];
  rowHighlightColors: ExportRowHighlightMap;
  modelSelectionBusy: boolean;
  exportFailuresBySystemName: ExportFailuresBySystemName;
  onOpenExportLog: () => void;
  systemTableRef: RefObject<HTMLDivElement | null>;
  systemToolsRef: RefObject<HTMLElement | null>;
  systemRowsRef: RefObject<HTMLElement | null>;
  firstSystemRowRef: RefObject<HTMLElement | null>;
  inlinePanelRef: RefObject<HTMLDivElement | null>;
  drawerPanelRef: RefObject<HTMLDivElement | null>;
  inlineSearchRef: RefObject<HTMLInputElement | null>;
  drawerSearchRef: RefObject<HTMLInputElement | null>;
  onSelectionChange: (selected: Set<string>) => void;
  onStatusClick?: (system: PipingSystem) => void;
  onSelectInModel: (systems: PipingSystem[]) => void;
  onCheckedChange: (id: string, checked: boolean) => void;
  onAdd: (property: PropertyItem) => void;
  onRemove: (id: string) => void;
  onClosePanel: () => void;
}

export default function ExportSystemsWorkspace(props: ExportSystemsWorkspaceProps) {
  return (
    <Box
      data-testid="export-pcf-main-area"
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        minHeight: 0,
        gap: props.columnPanelOpen && !props.useDrawer ? layoutGap.row : 0,
      }}
    >
      <Box
        ref={props.systemTableRef}
        data-testid="export-pcf-system-table-slot"
        tabIndex={-1}
        sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <SystemTable
          systems={props.systems}
          selected={props.selected}
          onSelectionChange={props.onSelectionChange}
          systemNameFilterValue={props.systemNameFilterValue}
          onSystemNameFilterChange={props.onSystemNameFilterChange}
          dynamicColumns={props.columns.checkedColumns}
          loading={props.loading}
          selectionDisabled={props.selectionDisabled}
          onStatusClick={props.onStatusClick}
          onSelectInModel={props.onSelectInModel}
          rowHighlightColors={props.rowHighlightColors}
          modelSelectionBusy={props.modelSelectionBusy}
          exportFailuresBySystemName={props.exportFailuresBySystemName}
          onOpenExportLog={props.onOpenExportLog}
          systemToolsRef={props.systemToolsRef}
          systemRowsRef={props.systemRowsRef}
          firstSystemRowRef={props.firstSystemRowRef}
        />
      </Box>
      <ExportColumnPanels
        open={props.columnPanelOpen}
        useDrawer={props.useDrawer}
        persistentDrawer={props.persistentDrawer}
        properties={props.panelProperties}
        extraProperties={props.columns.panelExtraProperties}
        availableParameters={props.availableParameters}
        inlinePanelRef={props.inlinePanelRef}
        drawerPanelRef={props.drawerPanelRef}
        inlineSearchRef={props.inlineSearchRef}
        drawerSearchRef={props.drawerSearchRef}
        onCheckedChange={props.onCheckedChange}
        onAdd={props.onAdd}
        onRemove={props.onRemove}
        onClose={props.onClosePanel}
      />
    </Box>
  );
}
